import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Spin, message, List, Badge, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ShopOutlined,
  InboxOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  WarningOutlined,
  FileTextOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { reportAPI, productAPI, customerAPI, supplierAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      monthlySales: { orderCount: 0, totalAmount: 0 },
      monthlyPurchase: { orderCount: 0, totalAmount: 0 },
      pendingOrders: { sales: 0, purchase: 0 },
      lowStockCount: 0,
      receivablePending: 0,
      payablePending: 0
    },
    salesTrend: [],
    topProducts: []
  });
  const [basicStats, setBasicStats] = useState({
    productCount: 0,
    customerCount: 0,
    supplierCount: 0
  });

  useEffect(() => {
    loadDashboardData();
    loadBasicStats();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const res = await reportAPI.getDashboard();
      if (res.data?.success) {
        setDashboardData(res.data.data);
      }
    } catch (error) {
      console.error('加载Dashboard数据失败:', error);
      message.error('加载Dashboard数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadBasicStats = async () => {
    try {
      const [productRes, customerRes, supplierRes] = await Promise.all([
        productAPI.getList({ page: 1, pageSize: 1 }),
        customerAPI.getList({ page: 1, pageSize: 1 }),
        supplierAPI.getList({ page: 1, pageSize: 1 })
      ]);
      setBasicStats({
        productCount: productRes.data?.data?.total || 0,
        customerCount: customerRes.data?.data?.total || 0,
        supplierCount: supplierRes.data?.data?.total || 0
      });
    } catch (error) {
      console.error('加载基础统计失败:', error);
    }
  };

  // 销售趋势图配置
  const salesTrendOption = {
    title: { text: '近7天销售趋势', left: 'center' },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const data = params[0];
        return `${data.name}<br/>销售额: ¥${Number(data.value).toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: dashboardData.salesTrend.map(item => item.date?.slice(5) || '')
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => `¥${(value / 1000).toFixed(0)}k`
      }
    },
    series: [{
      data: dashboardData.salesTrend.map(item => parseFloat(item.amount) || 0),
      type: 'line',
      smooth: true,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
            { offset: 1, color: 'rgba(24, 144, 255, 0.05)' }
          ]
        }
      },
      itemStyle: { color: '#1890ff' },
      lineStyle: { width: 2 }
    }],
    grid: { left: '10%', right: '5%', bottom: '10%', top: '15%' }
  };

  // 热销商品配置
  const topProductsOption = {
    title: { text: '本月热销商品 TOP5', left: 'center' },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => `¥${(value / 1000).toFixed(0)}k`
      }
    },
    yAxis: {
      type: 'category',
      data: dashboardData.topProducts.map(item => item.product?.name || '').reverse()
    },
    series: [{
      type: 'bar',
      data: dashboardData.topProducts.map(item => parseFloat(item.totalAmount) || 0).reverse(),
      itemStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 1, y2: 0,
          colorStops: [
            { offset: 0, color: '#1890ff' },
            { offset: 1, color: '#52c41a' }
          ]
        }
      },
      label: {
        show: true,
        position: 'right',
        formatter: (params) => `¥${Number(params.value).toFixed(0)}`
      }
    }],
    grid: { left: '25%', right: '15%', bottom: '10%', top: '15%' }
  };

  const { overview } = dashboardData;

  return (
    <Spin spinning={loading}>
      <div>
        <h2 style={{ marginBottom: 24 }}>工作台</h2>

        {/* 核心指标卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/sales-orders')}>
              <Statistic
                title="本月销售额"
                value={overview.monthlySales?.totalAmount || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#3f8600' }}
                suffix={<RiseOutlined />}
              />
              <div style={{ marginTop: 8, color: '#999' }}>
                订单数: {overview.monthlySales?.orderCount || 0} 单
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/purchase-orders')}>
              <Statistic
                title="本月采购额"
                value={overview.monthlyPurchase?.totalAmount || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#cf1322' }}
                suffix={<FallOutlined />}
              />
              <div style={{ marginTop: 8, color: '#999' }}>
                订单数: {overview.monthlyPurchase?.orderCount || 0} 单
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/finance/receivables')}>
              <Statistic
                title="待收款"
                value={overview.receivablePending || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#faad14' }}
              />
              <div style={{ marginTop: 8, color: '#999' }}>
                <DollarOutlined /> 应收账款待回收
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/finance/payables')}>
              <Statistic
                title="待付款"
                value={overview.payablePending || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#722ed1' }}
              />
              <div style={{ marginTop: 8, color: '#999' }}>
                <DollarOutlined /> 应付账款待支付
              </div>
            </Card>
          </Col>
        </Row>

        {/* 基础数据统计 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/products')}>
              <Statistic
                title="商品"
                value={basicStats.productCount}
                prefix={<ShoppingOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/customers')}>
              <Statistic
                title="客户"
                value={basicStats.customerCount}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/suppliers')}>
              <Statistic
                title="供应商"
                value={basicStats.supplierCount}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/inventory')}>
              <Statistic
                title="库存预警"
                value={overview.lowStockCount || 0}
                prefix={<WarningOutlined />}
                valueStyle={{ color: overview.lowStockCount > 0 ? '#cf1322' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/sales-orders')}>
              <Statistic
                title="待处理销售"
                value={overview.pendingOrders?.sales || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: overview.pendingOrders?.sales > 0 ? '#faad14' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card size="small" hoverable onClick={() => navigate('/purchase-orders')}>
              <Statistic
                title="待处理采购"
                value={overview.pendingOrders?.purchase || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: overview.pendingOrders?.purchase > 0 ? '#faad14' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={6}>
            <Card size="small" hoverable onClick={() => navigate('/reports')}>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <span style={{ fontSize: 16, color: '#1890ff' }}>查看更多报表 →</span>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 图表区域 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card>
              <ReactECharts option={salesTrendOption} style={{ height: 350 }} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card>
              <ReactECharts option={topProductsOption} style={{ height: 350 }} />
            </Card>
          </Col>
        </Row>

        {/* 热销商品明细 */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card
              title={<span><ShoppingCartOutlined /> 本月热销商品</span>}
              extra={<a onClick={() => navigate('/reports')}>更多报表</a>}
            >
              <Table
                dataSource={dashboardData.topProducts}
                columns={[
                  {
                    title: '排名',
                    key: 'rank',
                    width: 60,
                    render: (_, __, index) => (
                      <Badge
                        count={index + 1}
                        style={{
                          backgroundColor: index < 3 ? '#f5222d' : '#999',
                        }}
                      />
                    )
                  },
                  {
                    title: '商品',
                    key: 'product',
                    render: (_, record) => (
                      <div>
                        <div>{record.product?.name}</div>
                        <div style={{ color: '#999', fontSize: 12 }}>{record.product?.code}</div>
                      </div>
                    )
                  },
                  {
                    title: '销量',
                    dataIndex: 'totalQuantity',
                    key: 'totalQuantity',
                    align: 'center'
                  },
                  {
                    title: '销售额',
                    dataIndex: 'totalAmount',
                    key: 'totalAmount',
                    align: 'right',
                    render: (val) => <span style={{ color: '#3f8600' }}>¥{Number(val).toFixed(2)}</span>
                  }
                ]}
                pagination={false}
                size="small"
                rowKey={(record) => record.productId}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title={<span><ClockCircleOutlined /> 快捷操作</span>}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#e6f7ff' }}
                    onClick={() => navigate('/sales-orders')}
                  >
                    <ShoppingCartOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                    <div style={{ marginTop: 8 }}>新建销售订单</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#fff7e6' }}
                    onClick={() => navigate('/purchase-orders')}
                  >
                    <ShoppingOutlined style={{ fontSize: 24, color: '#faad14' }} />
                    <div style={{ marginTop: 8 }}>新建采购订单</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#f6ffed' }}
                    onClick={() => navigate('/products')}
                  >
                    <InboxOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                    <div style={{ marginTop: 8 }}>商品管理</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#f9f0ff' }}
                    onClick={() => navigate('/inventory')}
                  >
                    <InboxOutlined style={{ fontSize: 24, color: '#722ed1' }} />
                    <div style={{ marginTop: 8 }}>库存查询</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#e6fffb' }}
                    onClick={() => navigate('/finance/receivables')}
                  >
                    <DollarOutlined style={{ fontSize: 24, color: '#13c2c2' }} />
                    <div style={{ marginTop: 8 }}>收款管理</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    hoverable
                    size="small"
                    style={{ textAlign: 'center', background: '#fff1f0' }}
                    onClick={() => navigate('/finance/payables')}
                  >
                    <DollarOutlined style={{ fontSize: 24, color: '#f5222d' }} />
                    <div style={{ marginTop: 8 }}>付款管理</div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default Dashboard;
